import React, { useEffect, useState } from "react"
import { useSnapshot } from "valtio"
import { Step, StepOption } from "../../types/wizard"
import { Checkbox } from "../commons/Checkbox"
import { saveEmblemData, setShapeSizeStandard, validateWizardSteps, wizardStore, getImageAspectRatio } from "../../stores/wizard"
import RadioGroup from "./commonComps/RadioGroup"
import './QuickView.css'

export const Size: React.FC<{ step: Step }> = ({ step }) => {
  const wizSnap = useSnapshot(wizardStore)
  const [width, setWidth] = useState("")
  const [height, setHeight] = useState("")
  const [widthError, setWidthError] = useState("")
  const [heightError, setHeightError] = useState("")
  const [lockProportions, setLockProportions] = useState(true)
  const [imageAspectRatio, setImageAspectRatio] = useState<number | null>(null)
  const [isCalculating, setIsCalculating] = useState(false)

  const sizeOption = wizSnap.steps?.find((s) => s.id === step?.id)?.options[0]?.code ||
    wizSnap.steps?.find((s) => s.id === step?.id)?.options[0]?.name;
  const [selectedSizeOption, setSelectedSizeOption] = useState(wizSnap.emblemData[step?.id]?.code ?? sizeOption);
  const [useCompactLayout, setUseCompactLayout] = useState(window.innerWidth > 1200);

  useEffect(() => {
    const handleResize = () => {
      setUseCompactLayout(window.innerWidth > 1200);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const sizeData = wizSnap.emblemData?.[step.id]
    if (sizeData) {
      if (sizeData.width) setWidth(sizeData.width)
      if (sizeData.height) setHeight(sizeData.height)
    }
  }, [step.id, wizSnap.emblemData]);

  // Get image aspect ratio from uploaded image
  useEffect(() => {
    const aspectRatio = getImageAspectRatio();
    setImageAspectRatio(aspectRatio);
  }, [wizSnap.uploadedImageDimensions]);

  useEffect(() => {
    if (!imageAspectRatio || !lockProportions) return;

    if (width && !height) {
      calculateHeight(width, true);
    } else if (height && !width) {
      calculateWidth(height, true);
    } else if (width && height) {
      calculateHeight(width, true);
    }
  }, [imageAspectRatio, lockProportions]);

  useEffect(() => {
    if (step?.selectedValue && step?.selectedValue !== "custom" && step?.options?.length === 1) {
      saveEmblemData(step?.id, step?.options[0]);
      setShapeSizeStandard(step?.options[0]);
    }
  }, [step?.selectedValue]);

  const validateWidth = (value: number): boolean => {
    let errorMessage = ""
    const { widthMax, widthMin } = step.dimensionsConstraints;
    if ((value || value === 0) && (widthMax || widthMin)) {
      if (value > widthMax) errorMessage = `The maximum width is ${widthMax}"`
      else if (value < widthMin) errorMessage = `The minimum width is ${widthMin}"`
    }
    setWidthError(errorMessage)
    return !errorMessage
  }

  const validateHeight = (value: number): boolean => {
    let errorMessage = ""
    const { heightMax, heightMin } = step.dimensionsConstraints;
    if ((value || value === 0) && (heightMax || heightMin)) {
      if (value > heightMax) errorMessage = `The maximum height is ${heightMax}"`
      else if (value < heightMin) errorMessage = `The minimum height is ${heightMin}"`
    }
    setHeightError(errorMessage)
    return !errorMessage
  }

  const formatNumber = (value: number): string => {
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  };

  const saveDimensions = (widthValue?: string, heightValue?: string) => {
    const currentWidth = widthValue !== undefined ? widthValue : width;
    const currentHeight = heightValue !== undefined ? heightValue : height;

    saveEmblemData(step.id, {
      width: currentWidth || null,
      height: currentHeight || null,
      scaleWidth: false,
      scaleHeight: false
    });

    validateWizardSteps(step.id);
  };

  const calculateHeight = (widthValue: string, forceCalculate = false) => {
    if (!imageAspectRatio || (!lockProportions && !forceCalculate) || !widthValue || isCalculating) return;

    setIsCalculating(true);
    const w = parseFloat(widthValue);
    if (!isNaN(w) && w > 0) {
      const calculatedHeight = w / imageAspectRatio;
      const formattedHeight = formatNumber(calculatedHeight);
      setHeight(formattedHeight);

      validateWidth(w);
      validateHeight(calculatedHeight);
      saveDimensions(widthValue, formattedHeight);
    }
    requestAnimationFrame(() => setIsCalculating(false));
  };

  const calculateWidth = (heightValue: string, forceCalculate = false) => {
    if (!imageAspectRatio || (!lockProportions && !forceCalculate) || !heightValue || isCalculating) return;

    setIsCalculating(true);
    const h = parseFloat(heightValue);
    if (!isNaN(h) && h > 0) {
      const calculatedWidth = h * imageAspectRatio;
      const formattedWidth = formatNumber(calculatedWidth);
      setWidth(formattedWidth);

      validateHeight(h);
      validateWidth(calculatedWidth);
      saveDimensions(formattedWidth, heightValue);
    }
    requestAnimationFrame(() => setIsCalculating(false));
  };

  const handleWidth = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setWidth(value);

    if (value) {
      const isValid = validateWidth(Number(value));

      if (lockProportions && imageAspectRatio) {
        calculateHeight(value, false);
      } else if (isValid && height && validateHeight(Number(height))) {
        saveDimensions(value, height);
      }
    } else {
      saveDimensions("", height);
    }
  };

  const handleHeight = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setHeight(value);

    if (value) {
      const isValid = validateHeight(Number(value));

      if (lockProportions && imageAspectRatio) {
        calculateWidth(value, false);
      } else if (isValid && width && validateWidth(Number(width))) {
        saveDimensions(width, value);
      }
    } else {
      saveDimensions(width, "");
    }
  };

  const handleSizeChange = (selectedOption: StepOption) => {
    setSelectedSizeOption(selectedOption.name);
  };

  const handleLockProportions = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setLockProportions(isChecked);

    if (isChecked && imageAspectRatio) {
      if (width) {
        calculateHeight(width, true);
      } else if (height) {
        calculateWidth(height, true);
      }
    }
  };

  const isWidthCalculated = lockProportions && imageAspectRatio && height && !width;
  const isHeightCalculated = lockProportions && imageAspectRatio && width && !height;

  if (selectedSizeOption?.toLowerCase() === 'custom' && useCompactLayout) {
    return (
      <div className="w-full">
        <RadioGroup
          step={step}
          selectedValue={selectedSizeOption}
          onChange={handleSizeChange} />

        <div className="flex items-center justify-between mb-2.5">
          <label className="uppercase font-bold">Dimensions</label>
          {imageAspectRatio && (
            <div className="flex flex-row">
              <Checkbox
                checked={lockProportions}
                name="lockProportions"
                label="Lock Proportions"
                onChange={handleLockProportions}
              />
            </div>
          )}
        </div>

        <div className="flex items-start gap-x-3">
          {/* Width Input */}
          <div className="flex-1">
            <div className="relative">
              <input
                type="number"
                placeholder="Width"
                className={`p-2.5 pr-10 w-full rounded outline-none ${isWidthCalculated ? 'bg-orange-50 border-blue-300' : ''
                  }`}
                style={{ border: "1px solid #ccc" }}
                onChange={handleWidth}
                name="width"
                value={width}
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 font-bold text-gray-500">
                in.
              </span>
            </div>
            {widthError && (
              <span className="text-[#a61613] text-sm block mt-1">{widthError}</span>
            )}
          </div>

          <span className="text-gray-400 mt-2.5">×</span>

          {/* Height Input */}
          <div className="flex-1">
            <div className="relative">
              <input
                type="number"
                placeholder="Height"
                className={`p-2.5 pr-10 w-full rounded outline-none ${isHeightCalculated ? 'bg-orange-50 border-blue-300' : ''
                  }`}
                style={{ border: "1px solid #ccc" }}
                onChange={handleHeight}
                name="height"
                value={height}
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 font-bold text-gray-500">
                in.
              </span>
            </div>
            {heightError && (
              <span className="text-[#a61613] text-sm block mt-1">{heightError}</span>
            )}
          </div>
        </div>

        {wizSnap.errors[step?.id]?.length && (!heightError && !widthError) &&
          <div className="relative text-[#a61613] text-sm leading-4 mt-2">
            <span>{wizSnap.errors[step?.id][0]}</span>
          </div>
        }
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="w-full pb-2">
        <RadioGroup
          step={step}
          selectedValue={selectedSizeOption}
          onChange={handleSizeChange} />
      </div>
      {selectedSizeOption?.toLowerCase() === 'custom' &&
        <>
          {imageAspectRatio && (
            <div className="flex justify-end mb-4">
              <div className="flex flex-row">
                <Checkbox
                  checked={lockProportions}
                  name="lockProportions"
                  label="Lock Proportions"
                  onChange={handleLockProportions}
                />
              </div>
            </div>
          )}

          <div className="flex flex-col mb-4">
            <label className="uppercase font-bold mb-2.5">Width</label>
            <div className="flex items-center gap-x-4">
              <div className="relative w-full min-w-[120px] max-w-[100%]">
                <input
                  type="number"
                  className={`p-2.5 pr-10 w-full rounded outline-none border border-gray-300 ${isWidthCalculated ? 'bg-orange-50 border-blue-300' : ''
                    }`}
                  onChange={handleWidth}
                  name="width"
                  value={width}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 font-bold text-gray-500">
                  in.
                </span>
              </div>
            </div>
            {widthError && (
              <span className="relative text-[#a61613] text-sm mt-1">{widthError}</span>
            )}
          </div>

          <div className="flex flex-col mb-4">
            <label className="uppercase font-bold mb-2.5">Height</label>
            <div className="flex items-center gap-x-4">
              <div className="relative w-full min-w-[120px] max-w-[100%]">
                <input
                  type="number"
                  className={`p-2.5 pr-10 w-full rounded outline-none border border-gray-300 ${isHeightCalculated ? 'bg-orange-50 border-blue-300' : ''
                    }`}
                  onChange={handleHeight}
                  name="height"
                  value={height}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 font-bold text-gray-500">
                  in.
                </span>
              </div>
            </div>
            {heightError && (
              <span className="relative text-[#a61613] text-sm mt-1">{heightError}</span>
            )}
          </div>

          {wizSnap.errors[step?.id]?.length && (!heightError && !widthError) &&
            <div className="relative text-[#a61613] text-sm leading-4 mt-2">
              <span>{wizSnap.errors[step?.id][0]}</span>
            </div>
          }
        </>
      }
    </div>
  )
}